import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Layers,
  DollarSign,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { scenariosApi } from '../api/scenarios';
import { partsApi } from '../api/parts';

// New components for the redesign
import ComparisonSelector from '../components/scenarios/ComparisonSelector';
import ScenarioExecutiveSummary from '../components/scenarios/ScenarioExecutiveSummary';
import SOPOverviewTab from '../components/scenarios/SOPOverviewTab';
import ComponentBreakdownTable from '../components/scenarios/ComponentBreakdownTable';
import ScenarioWaterfallChart from '../components/charts/ScenarioWaterfallChart';

/**
 * ScenarioComparison - Redesigned enterprise-grade scenario comparison page
 * 
 * Inspired by Anaplan, SAP IBP, Digital Automotive
 * 
 * Features:
 * - Dynamic scenario selector (baseline vs comparison)
 * - Executive summary banner with KPIs
 * - Tabbed content: S&OP Overview, Cost Breakdown, Waterfall Analysis
 * - Professional UI with Lucide icons (no emojis)
 */
export default function ScenarioComparison() {
  const { partId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programId = searchParams.get('programId');
  
  // State
  const [part, setPart] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [baselineId, setBaselineId] = useState(null);
  const [comparisonId, setComparisonId] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [costImpactData, setCostImpactData] = useState(null);
  const [scenariosCompareData, setScenariosCompareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Derived state
  const baselineScenario = useMemo(() => 
    scenarios.find(s => s.id === baselineId), 
    [scenarios, baselineId]
  );
  
  const comparisonScenario = useMemo(() => 
    scenarios.find(s => s.id === comparisonId), 
    [scenarios, comparisonId]
  );

  const hasValidComparison = baselineId && comparisonId && baselineId !== comparisonId;

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, [partId, programId]);

  // Auto-compare when selection changes
  useEffect(() => {
    if (hasValidComparison) {
      fetchComparisonData();
    } else {
      setComparisonData(null);
      setCostImpactData(null);
      setScenariosCompareData(null);
    }
  }, [baselineId, comparisonId, programId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch part details
      const partResponse = await partsApi.getPartDetails(partId, { programId });
      if (partResponse.success) {
        setPart(partResponse.data);
      }

      // Fetch scenarios
      const scenariosResponse = await scenariosApi.getScenarios(partId, { programId });
      if (scenariosResponse.success) {
        const scenarioList = scenariosResponse.data || [];
        setScenarios(scenarioList);
        
        // Auto-select: active scenario as baseline, first non-active as comparison
        const active = scenarioList.find(s => s.is_active);
        const firstOther = scenarioList.find(s => !s.is_active) || scenarioList[1];
        
        if (active) {
          setBaselineId(active.id);
          if (firstOther && firstOther.id !== active.id) {
            setComparisonId(firstOther.id);
          }
        } else if (scenarioList.length >= 2) {
          setBaselineId(scenarioList[0].id);
          setComparisonId(scenarioList[1].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load scenario data');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparisonData = async () => {
    if (!hasValidComparison) return;
    
    setComparing(true);
    setError(null);
    
    try {
      // Fetch both cost-impact and compare APIs in parallel
      const [costImpactResponse, compareResponse] = await Promise.all([
        scenariosApi.getCostImpact(partId, {
          baselineScenarioId: baselineId,
          currentScenarioId: comparisonId,
          programId,
        }),
        scenariosApi.compareScenarios(partId, [baselineId, comparisonId], { programId }),
      ]);

      if (costImpactResponse.success) {
        setCostImpactData(costImpactResponse.data);
      }

      if (compareResponse.success) {
        setScenariosCompareData(compareResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch comparison data:', error);
      setError('Failed to compare scenarios');
    } finally {
      setComparing(false);
    }
  };

  const handleSwap = () => {
    const temp = baselineId;
    setBaselineId(comparisonId);
    setComparisonId(temp);
  };

  const handleRefresh = () => {
    fetchComparisonData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading comparison data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(programId ? `/parts/${partId}?programId=${programId}` : `/parts/${partId}`)}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Back to Part Detail
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Scenario Comparison</h1>
            <p className="text-gray-600">
              {part?.part_number} - {part?.description}
            </p>
          </div>
        </div>
        
        {/* Refresh Button */}
        {hasValidComparison && (
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={comparing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${comparing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        )}
      </div>

      {/* Scenario Selector */}
      <ComparisonSelector
        scenarios={scenarios}
        baselineId={baselineId}
        comparisonId={comparisonId}
        onBaselineChange={setBaselineId}
        onComparisonChange={setComparisonId}
        onSwap={handleSwap}
        loading={comparing}
      />

      {/* Error Alert */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* No Selection Message */}
      {!hasValidComparison && !error && (
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-8 text-center">
            <Layers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Select Scenarios to Compare</h3>
            <p className="text-gray-500">Choose a baseline and comparison scenario above to see the analysis.</p>
          </CardContent>
        </Card>
      )}

      {/* Main Content - Only show when comparison is valid */}
      {hasValidComparison && !error && (
        <>
          {/* Executive Summary */}
          {costImpactData && (
            <ScenarioExecutiveSummary
              data={costImpactData}
              baselineScenario={baselineScenario}
              comparisonScenario={comparisonScenario}
            />
          )}

          {/* Tabbed Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                <span>S&OP Overview</span>
              </TabsTrigger>
              <TabsTrigger value="breakdown" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                <span>Cost Breakdown</span>
              </TabsTrigger>
              <TabsTrigger value="waterfall" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                <span>Waterfall Analysis</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <SOPOverviewTab
                comparisonData={costImpactData}
                baselineScenario={baselineScenario}
                comparisonScenario={comparisonScenario}
                scenariosData={scenariosCompareData?.scenarios}
              />
            </TabsContent>

            <TabsContent value="breakdown" className="mt-6">
              <ComponentBreakdownTable
                data={costImpactData}
                baselineScenario={baselineScenario}
                comparisonScenario={comparisonScenario}
              />
            </TabsContent>

            <TabsContent value="waterfall" className="mt-6">
              <ScenarioWaterfallChart
                data={costImpactData}
                baselineScenario={baselineScenario}
                comparisonScenario={comparisonScenario}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Loading overlay */}
      {comparing && (
        <div className="fixed inset-0 bg-white/50 flex items-center justify-center z-50">
          <Card className="p-6 shadow-lg">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
              <span className="text-gray-700">Comparing scenarios...</span>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
